import React, { useState, useEffect, useRef, useCallback } from 'react';

const AlumniSection = () => {
  const [alumniImages, setAlumniImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const intervalRef = useRef(null);
  const SLIDES_PER_VIEW = 6;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchAlumni = async () => {
      try {
        const res = await fetch('./admin/api/alumni.php');
        const data = await res.json();
        if (data.success) setAlumniImages(data.data);
      } catch { /* silent */ } finally {
        setLoading(false);
      }
    };
    fetchAlumni();
  }, []);

  const totalSlides = Math.ceil(alumniImages.length / SLIDES_PER_VIEW);

  const next = useCallback(() => {
    setCurrentSlide(p => (p + 1) % totalSlides);
  }, [totalSlides]);

  const prev = useCallback(() => {
    setCurrentSlide(p => (p - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  useEffect(() => {
    if (!isPlaying || totalSlides <= 1) return;
    intervalRef.current = setInterval(next, 3500);
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, next, totalSlides]);

  const openModal = (img) => {
    setSelectedImage(img);
    setIsPlaying(false);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedImage(null);
    setIsPlaying(true);
    document.body.style.overflow = '';
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="flex flex-col items-center gap-4 py-12">
          <div className="spinner" />
          <p className="text-gray-400 text-sm font-inter">Loading alumni...</p>
        </div>
      </section>
    );
  }

  if (alumniImages.length === 0) return null;

  return (
    <section ref={ref} className="py-20 sm:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="section-badge">
            <i className="fas fa-user-graduate" />
            Our Alumni
          </div>
          <h2 className="section-title">Celebrating Our Distinguished Alumni</h2>
          <p className="section-subtitle mx-auto">
            Proud of our alumni who continue to make us proud with their achievements and contributions.
          </p>
        </div>

        {/* Slider */}
        <div
          className={`relative transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          onMouseEnter={() => setIsPlaying(false)}
          onMouseLeave={() => setIsPlaying(true)}
        >
          {/* Prev */}
          {totalSlides > 1 && (
            <button
              onClick={prev}
              className="absolute -left-4 sm:-left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center text-gray-600 hover:text-primary hover:border-primary/30 transition-all duration-200 hover:scale-105"
              aria-label="Previous slide"
            >
              <i className="fas fa-chevron-left text-sm" />
            </button>
          )}

          {/* Track */}
          <div className="overflow-hidden rounded-2xl">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {Array.from({ length: totalSlides }).map((_, slideIdx) => (
                <div key={slideIdx} className="w-full flex-shrink-0">
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3">
                    {alumniImages
                      .slice(slideIdx * SLIDES_PER_VIEW, (slideIdx + 1) * SLIDES_PER_VIEW)
                      .map((alumni, i) => (
                        <div
                          key={`${slideIdx}-${i}`}
                          className="group relative overflow-hidden rounded-xl cursor-pointer bg-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                          style={{ aspectRatio: '1/1' }}
                          onClick={() => openModal(alumni)}
                          role="button"
                          tabIndex={0}
                          aria-label={`View ${alumni.title || 'alumni photo'}`}
                          onKeyDown={(e) => e.key === 'Enter' && openModal(alumni)}
                        >
                          <img
                            src={`admin/${alumni.image_path}`}
                            alt={alumni.title || 'Alumni'}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-400"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/30 transition-all duration-300 flex items-center justify-center">
                            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center scale-0 group-hover:scale-100 transition-transform duration-300">
                              <i className="fas fa-search-plus text-navy text-xs" />
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Next */}
          {totalSlides > 1 && (
            <button
              onClick={next}
              className="absolute -right-4 sm:-right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center text-gray-600 hover:text-primary hover:border-primary/30 transition-all duration-200 hover:scale-105"
              aria-label="Next slide"
            >
              <i className="fas fa-chevron-right text-sm" />
            </button>
          )}
        </div>

        {/* Dots */}
        {totalSlides > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: totalSlides }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`rounded-full transition-all duration-300 ${i === currentSlide ? 'w-6 h-2 bg-primary' : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'}`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedImage && (
        <div
          className="lightbox-overlay"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            onClick={closeModal}
            aria-label="Close"
          >
            <i className="fas fa-times" />
          </button>
          <div
            className="bg-white rounded-2xl overflow-hidden shadow-2xl max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`admin/${selectedImage.image_path}`}
              alt={selectedImage.title || 'Alumni'}
              className="w-full max-h-[70vh] object-contain"
            />
            {selectedImage.title && (
              <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                <h3 className="font-poppins font-600 text-navy text-sm">{selectedImage.title}</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 text-sm">
                  Close ✕
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default AlumniSection;
